<template>
	<div class="sr radius-small border-100 border-high-active bg-medium" @click="emits('onPosterClickOut')">
		<div class="sr__container p-6">
			<div class="sr__contentWrapper">
				<div class="sm:hidden">
					<slot name="provider-icon"></slot>
				</div>

				<div class="sr__content sm:g-6">
					<div class="sr__titlePoster radius-small mx-5 sm:mx-0">
						<PictureComp
							v-if="posterSRImage"
							:alt="campaignNode?.content.title"
							:sponsoredRecImages="posterSRImage"
							image-profile="poster"
							@imageError="src => $emit('imageError', src)"
						/>
					</div>

					<div class="sr__titleContent g-6">
						<div class="sr__providerWrapper sm:show">
							<slot name="provider-icon"></slot>
						</div>

						<slot name="campaign-info">
							<div class="sr__titleInfo g-4">
								<h2 class="sr__title text-high align-content m-0" data-testid="sr__title">
									{{ promotionalTitle }}
								</h2>

								<div
									v-if="promotionalText"
									class="sr__promoText text-high align-content"
									:class="{
										'sm:show': hasTimeRemaining && getReleaseDate,
									}"
								>
									{{ promotionalText }}
								</div>

								<CountdownTimer
									v-if="hasTimeRemaining && getReleaseDate"
									:dateTime="getReleaseDate"
									class="align-content"
								/>
							</div>
						</slot>

						<div class="align-content">
							<slot name="action-items"></slot>
							<div v-if="sponsoredAdCampaign?.disclaimerText" class="sr__disclaimer pt-2">
								{{ sponsoredAdCampaign?.disclaimerText }}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="sr__imgContainer">
			<picture>
				<template v-if="backgroundImages?.SMALL">
					<source
						type="image/avif"
						:srcset="backgroundImages.SMALL.replace(/\.\w+$/, '.avif')"
						media="(max-width: 767px)"
					/>
					<source :srcset="backgroundImages.SMALL" media="(max-width: 767px)" />
				</template>

				<template v-if="backgroundImages?.MEDIUM">
					<source
						type="image/avif"
						:srcset="backgroundImages.MEDIUM.replace(/\.\w+$/, '.avif')"
						media="(min-width: 768px) and (max-width: 1200px)"
					/>
					<source :srcset="backgroundImages.MEDIUM" media="(min-width: 768px) and (max-width: 1200px)" />
				</template>

				<source type="image/avif" :srcset="backgroundImages?.LARGE.replace(/\.\w+$/, '.avif')" />

				<img
					:src="backgroundImages?.LARGE"
					:alt="campaignNode?.content.title"
					loading="eager"
					class="sr__img"
					@imageError="$emit('imageError', backgroundImages?.LARGE)"
				/>
			</picture>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';

import CountdownTimer from '@/features/sponsored-recommendations/components/CountdownTimer.vue';
import PictureComp from '@/components/picture/PictureComp.vue';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { PosterSponsoredRecImages } from '@/interfaces/components/poster/PosterGraphql';
import { useCampaignInfo } from '@/features/sponsored-recommendations/composables/useSendBidId';

import { BackgroundImageSize } from '@/@types/graphql-types';
import type { Maybe } from '@/helpers/types';

const props = withDefaults(
	defineProps<{
		sponsoredAdCampaign: NonNullable<SponsoredAdFragment['campaign']>;
		lazy?: boolean;
		additionalContexts?: SnowplowContext[];
	}>(),
	{
		lazy: true,
		additionalContexts: () => [],
	}
);

const emits = defineEmits<{
	(e: 'onPosterClickOut'): void;
	(e: 'imageError', src: Maybe<string>): void;
}>();

const { jwConfig } = useRuntimeConfig().public;

const { campaignNode, promotionalTitle, promotionalText } = useCampaignInfo(() => props.sponsoredAdCampaign);

type ImageObject = { [key in BackgroundImageSize]: string };
const backgroundImages = computed(() => {
	const imageSizes: ImageObject = {} as ImageObject;

	props.sponsoredAdCampaign?.backgroundImages?.map(options => {
		if (!options?.size) {
			return;
		}

		imageSizes[options.size] = options?.imageURL;
	});

	return imageSizes;
});

const getReleaseDate = computed<Date | undefined>(() => {
	if (!import.meta.client || !props.sponsoredAdCampaign?.countdownTimer) return undefined;

	return new Date(props.sponsoredAdCampaign?.countdownTimer);
});

// Title Poster (to support AVIF)
const posterSRImage = computed<PosterSponsoredRecImages | null>(() => {
	let posterUrl = props.sponsoredAdCampaign?.posterOverride || campaignNode?.value.content.posterUrl;
	if (!posterUrl) return null;

	if (posterUrl.startsWith('/')) {
		posterUrl = `${jwConfig.IMAGESCALER_URL}${posterUrl.replace('{profile}', 's332')}`;
	}

	return {
		imageUrl: posterUrl,
		sourceSets: [
			{
				srcSet: posterUrl.replace(/\.{?\w+}?$/, '.avif'),
				imageProp: {
					extension: '.avif',
					type: 'image/avif',
				},
				sourceProp: {
					viewport: 'desktop',
				},
				media: null,
			},
		],
	};
});

// Manage check to display countdown
onMounted(() => checkIfHasTimeRemaining());
onUnmounted(() => clearInterval(timerToMatchStarts));
const timerToMatchStarts = setInterval(checkIfHasTimeRemaining, 1000);

const hasTimeRemaining = ref(true);

function checkIfHasTimeRemaining() {
	// If there is no release time, hide countdown
	if (!import.meta.client || !getReleaseDate.value) {
		hasTimeRemaining.value = false;
		return;
	}

	const currentTime = new Date();

	// If release time is after the currentTime
	if (currentTime.getTime() >= getReleaseDate.value.getTime()) {
		hasTimeRemaining.value = false;
		return;
	}

	hasTimeRemaining.value = true;
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/rtl-support-mixins.scss';

.sr {
	position: relative;
	container: sponsoredRecommendation / inline-size;
	width: 100%;

	cursor: pointer;

	display: flex;
	flex-direction: column;
	overflow: hidden;

	@media (min-width: $screen-sm) {
		display: block;
	}

	.align-content {
		align-self: center;

		@media (min-width: $screen-sm) {
			align-self: flex-start;
		}
	}

	&__content {
		display: flex;
		flex-direction: column;
		align-items: center;

		@media (min-width: $screen-sm) {
			flex-direction: row;
			align-items: normal;
		}
	}

	&__titlePoster {
		overflow: hidden;

		:deep(.picture-comp__img) {
			width: auto;
			height: var(--jw-size-23);

			@media (min-width: $screen-sm) {
				height: var(--jw-size-23);
			}
		}
	}

	&__contentWrapper {
		display: flex;
		flex-direction: column;
		z-index: 2;
	}

	&__titleContent {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
	}

	&__imgContainer {
		position: absolute;
		top: 0;
		right: 0;
		height: 100%;
		width: 100%;
		border-radius: 0 0 var(--jw-radius-small) var(--jw-radius-small);

		@media (min-width: $screen-sm) {
			border-radius: var(--jw-radius-small);
			aspect-ratio: 21/9;
			width: unset;
		}

		@media (min-width: $screen-lg) {
			aspect-ratio: 4/1;
		}

		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			z-index: 1;
			pointer-events: none; /* Makes sure overlay doesn’t interfere with interactions */

			@media (min-width: $screen-sm) {
				background: linear-gradient(
					to right,
					rgba(#111924, 1) 0%,
					rgba(#111924, 0.987) 6.4%,
					rgba(#111924, 0.951) 11.6%,
					rgba(#111924, 0.896) 16.1%,
					rgba(#111924, 0.825) 19.9%,
					rgba(#111924, 0.741) 23.5%,
					rgba(#111924, 0.648) 27%,
					rgba(#111924, 0.55) 30.7%,
					rgba(#111924, 0.45) 34.9%,
					rgba(#111924, 0.352) 39.9%,
					rgba(#111924, 0.259) 45.9%,
					rgba(#111924, 0.175) 53.1%,
					rgba(#111924, 0.104) 61.9%,
					rgba(#111924, 0.049) 72.4%,
					rgba(#111924, 0.013) 85%,
					rgba(#111924, 0) 100%
				);
			}
		}
	}

	&__img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		filter: blur(48px);
		z-index: 0;

		@media (min-width: $screen-sm) {
			filter: blur(0);
		}

		.sr--PREMIUM_HOME & {
			object-position: 60% center;
		}
	}

	&__container {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}

	&__title {
		font-weight: 700;
		font-size: 24px;
		line-height: 1.2;
		white-space: pre-line;
	}

	&__titleInfo {
		display: flex;
		flex-direction: column;
	}

	&__promoText {
		font-size: 16px;
	}

	&__disclaimer {
		font-size: 12px;
	}
}

//remove opacity on ios devices as it causes flickering
html.ios .sr:active {
	opacity: unset;
}
</style>
